
import { FileDownloadOutlined, InfoOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardHeader, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { actionTwoButtonStyle, cardStyle } from '../../theme';


const CampaignCallDetailsDialog = ({ open, data, campaignId, inputStartDate, inputEndDate, segments, campaignName }) => {
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const systemAgents = ['0000000', 'System', 'SS Service', 'RingCX']

	const [agentRetryDispos, setAgentRetryDispos] = useState([]);
	const [agentFinalDispos, setAgentFinalDispos] = useState([]);
	const [systemRetryDispos, setSystemRetryDispos] = useState([]);
	const [systemFinalDispos, setSystemFinalDispos] = useState([]);


	useEffect(() => {
		if (open) {
			console.log(segments)
			setLoading(true);
			for (const status of data) {
				const systemDispositions = [];
				const agentDispositions = [];

				for (const disposition of status.disposition.buckets) {
					if (disposition.agent.buckets) {
						let agentCount = 0;
						let systemCount = 0;
						for (const agent of disposition.agent.buckets) {
							if (systemAgents.includes(agent.key) || !agent.key) {
								systemCount = systemCount + agent.doc_count;
							} else {
								agentCount = agentCount + agent.doc_count;
							}
						}
						if (systemCount > 0) {
							systemDispositions.push({ label: disposition.key, value: systemCount });
						}
						if (agentCount > 0) {
							agentDispositions.push({ label: disposition.key, value: agentCount, agents: disposition.agent.buckets.sort((a, b) => b.doc_count - a.doc_count) });
						}
					}
				}
				if (status.key === 'retry') {
					// Sort from highest to lowest
					agentDispositions.sort((a, b) => b.value - a.value);
					systemDispositions.sort((a, b) => b.value - a.value);
					setSystemRetryDispos(systemDispositions);
					setAgentRetryDispos(agentDispositions);
				} else {
					// Sort from highest to lowest
					agentDispositions.sort((a, b) => b.value - a.value);
					systemDispositions.sort((a, b) => b.value - a.value);
					setSystemFinalDispos(systemDispositions);
					setAgentFinalDispos(agentDispositions);
				}
			}
			setLoading(false);
		}
	}, [open]);

	function GetRow(props) {
		const [open, setOpen] = useState(false);
		const { disposition, agent, status } = props;
		const isSystem = agent === 'system';

		return (
			<>
				<TableRow key={disposition.label} sx={{ cursor: isSystem ? 'default' : 'pointer' }}>
					{!isSystem && disposition?.agents && <TableCell onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}</TableCell>}
					{!isSystem && !disposition?.agents && <TableCell />}
					{isSystem && <TableCell />}
					<TableCell onClick={!isSystem ? () => setOpen(!open) : undefined}>
						{disposition.label}
						{disposition.label === 'Agent Specific — Agent Unavailable' && <Tooltip title="This disposition appears when before placing the dial, there are no agents available to take the call. Instead of dialing the contact, the system will send in a suppressed disposition. These dials won't appear on your contact center reporting">
							<InfoOutlined color="primary" />
						</Tooltip>}
					</TableCell>
					<TableCell onClick={!isSystem ? () => setOpen(!open) : undefined}>{disposition.value}</TableCell>
					<TableCell align="right">
						<Tooltip title='Download CSV'>
							<IconButton onClick={() => {
								downloadCSV(status, agent, disposition.label);
							}}>
								<FileDownloadOutlined color="primary" />
							</IconButton>
						</Tooltip>
					</TableCell>
				</TableRow>
				{!isSystem && <TableRow key={disposition.label + 'details'} sx={{ display: open ? 'table-row' : 'none' }}>
					<TableCell colSpan={4}>
						<Table sx={{ width: '100%' }}>
							<TableHead>
								<TableRow>
									<TableCell>Agent</TableCell>
									<TableCell>Count</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{disposition?.agents?.map((agent) => (
									<TableRow key={agent.key}>
										<TableCell>{agent.key}</TableCell>
										<TableCell>{agent.doc_count}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableCell>
				</TableRow>}
			</>
		);
	}

	const downloadCSV = async (status, agentType, dispositionFilter) => {
		try {
			const session = await fetchAuthSession();
			await post({
				apiName: 'cdyxoutreach',
				path: '/export/call-details',
				options: {
					body: {
						campaignId,
						campaignName,
						startDate: inputStartDate ? inputStartDate : moment().startOf('day').toISOString(),
						endDate: inputEndDate ? inputEndDate : moment().toISOString(),
						status: status || null,
						agentType: agentType || null,
						dispositionFilter: dispositionFilter || null,
						segments,
						hideExternalId: userContext.telephonyProvider === 'Webex'
					},
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey
					}
				}
			}).response;

			enqueueSnackbar('Report generation started. You will receive a notification when it is ready.', { variant: 'success' });
		} catch (err) {
			console.error('Failed to request call details report:', err);
			enqueueSnackbar('Failed to start report generation.', { variant: 'error' });
		}
	}

	return (
		<Box display='flex' flexDirection="column" justifyContent='center' sx={{ width: '100%' }} gap={2}>
			{!loading && <Box sx={{ width: '100%' }} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>
					} />
					<Table sx={{ width: '100%' }}>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentFinalDispos.map((disposition) => <GetRow disposition={disposition} status='completed' agent='agent' />)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentRetryDispos.map((disposition) => <GetRow disposition={disposition} status='retry' agent='agent' />)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>} />
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemFinalDispos.map((disposition) => <GetRow disposition={disposition} status='completed' agent='system' />)}

						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemRetryDispos.map((disposition) => <GetRow disposition={disposition} status='retry' agent='system' />)}
						</TableBody>
					</Table>
				</Card>

			</Box>}
			{!loading && <Button sx={actionTwoButtonStyle} onClick={() => downloadCSV()} >Download All Data</Button>}
			{loading ? <CircularProgress /> : null}
		</Box>

	);
};

export default CampaignCallDetailsDialog;
